import React, { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Button } from "components/ui";
import { 
  XMarkIcon, 
  ClockIcon, 
  UserIcon, 
  PencilIcon, 
  TrashIcon,
  ChatBubbleBottomCenterTextIcon,
  CurrencyDollarIcon,
  TagIcon,
  UserCircleIcon,
  SparklesIcon
} from "@heroicons/react/24/outline";
import { EntityTypeEnum } from "enum/EntityTypeEnum";
import { EntityDynamicFieldsView } from "components/shared/dynamic-fields";
import { formatDisplayDate } from "utils/dateUtils";

const defaultGetWorkStatusColor = (statusName) => {
  const name = statusName?.toLowerCase() || "";
  if (name === "completed") {
    return "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20";
  }
  if (name === "delivered") {
    return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20";
  }
  if (name === "ordered") {
    return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20";
  }
  return "bg-gray-500/10 text-gray-700 dark:text-gray-400 border border-gray-500/20";
};

const defaultGetCostStatusColor = (statusName) => {
  const name = statusName?.toLowerCase() || "";
  if (name === "invoiced to client") {
    return "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20";
  }
  if (name === "standard") {
    return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20";
  }
  return "bg-gray-500/10 text-gray-700 dark:text-gray-400 border border-gray-500/20";
};

export default function ViewNoteModal({
  show,
  onClose,
  selectedQuotation,
  getWorkStatusColor = defaultGetWorkStatusColor,
  getCostStatusColor = defaultGetCostStatusColor,
  users = [],
  workStatuses = [],
  costStatuses = [],
  availableColumns = [],
  onEdit,
  onDelete,
}) {
  if (!selectedQuotation) return null;

  const workStatusName = 
    selectedQuotation.WorkStatusName ||
    selectedQuotation.WorkStatus ||
    selectedQuotation.workStatusName ||
    workStatuses.find(s => String(s.Id) === String(selectedQuotation.WorkStatusId))?.StatusName ||
    null;

  const costStatusName = 
    selectedQuotation.CostStatusName ||
    selectedQuotation.CostStatus ||
    selectedQuotation.costStatusName ||
    costStatuses.find(s => String(s.Id) === String(selectedQuotation.CostStatusId))?.StatusName ||
    null;

  const assignedUserName = 
    selectedQuotation.AssignedToName ||
    selectedQuotation.AssignedTo ||
    users.find(u => String(u.userId || u.UserId) === String(selectedQuotation.AssignedToUserId))?.fullName ||
    users.find(u => String(u.userId || u.UserId) === String(selectedQuotation.AssignedToUserId))?.FullName ||
    null;

  const costAssignedUserName = 
    selectedQuotation.CostAssignedToName ||
    selectedQuotation.CostAssignedTo ||
    users.find(u => String(u.userId || u.UserId) === String(selectedQuotation.CostAssignedToUserId))?.fullName ||
    users.find(u => String(u.userId || u.UserId) === String(selectedQuotation.CostAssignedToUserId))?.FullName ||
    null;

  const resolveUserName = (userIdOrName) => {
    if (!userIdOrName) return "";
    const isGuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(userIdOrName);
    if (isGuid) {
      const foundUser = users.find(u => String(u.userId || u.UserId || u.id).toLowerCase() === String(userIdOrName).toLowerCase());
      return foundUser ? (foundUser.fullName || foundUser.FullName) : "Unknown User";
    }
    return userIdOrName;
  };

  const createdByName = selectedQuotation.CreatedByName || resolveUserName(selectedQuotation.CreatedBy || selectedQuotation.createdBy);
  const modifiedByName = selectedQuotation.ModifiedByName || resolveUserName(selectedQuotation.ModifiedBy || selectedQuotation.modifiedBy);

  const rawWish = 
    selectedQuotation.ClientWish || 
    selectedQuotation.clientWish || 
    selectedQuotation.Wish || 
    selectedQuotation.WishItemName || 
    selectedQuotation.wishItemName || 
    selectedQuotation.wish || 
    "";

  const formattedCost = (() => {
    if (!selectedQuotation.Cost) return null;
    let str = String(selectedQuotation.Cost).replace(/\$/g, "A$");
    if (/^[\d,.]/.test(str) && !str.includes("A$")) str = `A$${str}`;
    return str;
  })();

  const renderFormattedHtml = (content) => {
    if (!content) return <span className="text-slate-400 dark:text-dark-500 italic">-</span>;
    
    const containsHtml = /<[a-z][\s\S]*>/i.test(content);
    if (containsHtml) {
      return (
        <div 
          className="prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-100 leading-relaxed font-normal [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>h1]:text-lg [&>h1]:font-bold [&>h2]:text-base [&>h2]:font-bold [&>h3]:text-sm [&>h3]:font-semibold [&>blockquote]:border-l-4 [&>blockquote]:border-indigo-400 [&>blockquote]:pl-3 [&>blockquote]:italic"
          dangerouslySetInnerHTML={{ __html: content }} 
        />
      );
    }

    const paragraphs = content.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (paragraphs.length > 1) {
      return (
        <div className="space-y-2 text-slate-800 dark:text-slate-100 font-normal leading-relaxed text-sm">
          {paragraphs.map((p, idx) => (
            <p key={idx}>{p}</p>
          ))}
        </div>
      );
    }

    return <span className="text-slate-800 dark:text-slate-100 font-normal leading-relaxed text-sm">{content}</span>;
  };

  const hasAssignee = assignedUserName && assignedUserName !== "-";
  const hasCostAssignee = costAssignedUserName && costAssignedUserName !== "-";
  const hasDynamicFields = selectedQuotation.DynamicFields && selectedQuotation.DynamicFields.length > 0;

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-h-[90vh] max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-dark-900 shadow-2xl transition-all flex flex-col outline-none focus:outline-none border border-slate-200/80 dark:border-dark-800">
                
                {/* ── Header ── */}
                <div className="flex-shrink-0 border-b border-slate-150 dark:border-dark-800 px-6 py-4 flex items-center justify-between bg-white dark:bg-dark-900">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                      <SparklesIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <DialogTitle as="h3" className="text-base font-bold text-slate-900 dark:text-white leading-tight">
                        Client Note Details
                      </DialogTitle>
                      {selectedQuotation.CategoryName && (
                        <p className="text-xs font-medium text-slate-500 dark:text-dark-400 mt-0.5">
                          Category: <span className="text-slate-800 dark:text-dark-200 font-semibold">{selectedQuotation.CategoryName}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {onEdit && (
                      <button
                        onClick={() => {
                          onEdit(selectedQuotation);
                          onClose();
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-400 transition-colors"
                        title="Edit Note"
                      >
                        <PencilIcon className="h-4.5 w-4.5" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          onDelete(selectedQuotation.Id);
                          onClose();
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors"
                        title="Delete Note"
                      >
                        <TrashIcon className="h-4.5 w-4.5" />
                      </button>
                    )}
                    <div className="h-4 w-px bg-slate-200 dark:bg-dark-700 mx-1" />
                    <Button
                      onClick={onClose}
                      variant="flat"
                      isIcon
                      className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-dark-800 dark:hover:text-dark-200 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* ── Body Content ── */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-white dark:bg-dark-900">
                  
                  {/* Badges Bar (Note Type, Cost, Work Status, Cost Status, Assignees) */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Note Type */}
                    {selectedQuotation.NoteType && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50/80 dark:bg-indigo-950/40 px-2.5 py-1 rounded-lg border border-indigo-100 dark:border-indigo-900/30">
                        <TagIcon className="w-3.5 h-3.5 text-indigo-500" />
                        Type: {selectedQuotation.NoteType}
                      </span>
                    )}

                    {/* Cost */}
                    {formattedCost && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50/80 dark:bg-emerald-950/40 px-2.5 py-1 rounded-lg border border-emerald-100 dark:border-emerald-900/30">
                        <CurrencyDollarIcon className="w-3.5 h-3.5 text-emerald-500" />
                        Cost: {formattedCost}
                      </span>
                    )}

                    {/* Work Status */}
                    {workStatusName && workStatusName !== "-" && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg ${getWorkStatusColor ? getWorkStatusColor(workStatusName) : "bg-slate-100 text-slate-700 dark:bg-dark-700 dark:text-dark-200"}`}>
                        Status: {workStatusName}
                      </span>
                    )}

                    {/* Cost Status */}
                    {costStatusName && costStatusName !== "-" && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg ${getCostStatusColor ? getCostStatusColor(costStatusName) : "bg-slate-100 text-slate-700 dark:bg-dark-700 dark:text-dark-200"}`}>
                        Cost Status: {costStatusName}
                      </span>
                    )}

                    {/* Assigned To (ONLY if data exists) */}
                    {hasAssignee && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-dark-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-dark-700">
                        <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                        Assigned To: <strong className="font-semibold text-slate-900 dark:text-white">{assignedUserName}</strong>
                      </span>
                    )}

                    {/* Cost Status Assignee (ONLY if data exists) */}
                    {hasCostAssignee && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-dark-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-dark-700">
                        <UserCircleIcon className="w-3.5 h-3.5 text-slate-400" />
                        Cost Assignee: <strong className="font-semibold text-slate-900 dark:text-white">{costAssignedUserName}</strong>
                      </span>
                    )}
                  </div>

                  {/* ── Client Wish Section ── */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-dark-400">
                      <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                      <span>Client Wish</span>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50/60 dark:bg-dark-800/50 border border-slate-150 dark:border-dark-750">
                      {renderFormattedHtml(rawWish)}
                    </div>
                  </div>

                  {/* Entity Dynamic Fields View */}
                  <EntityDynamicFieldsView
                    entityTypeCode={selectedQuotation.JobId ? EntityTypeEnum.JobClientNote : EntityTypeEnum.LeadClientNote}
                    entityId={selectedQuotation.Id}
                  />

                </div>

                {/* ── Modal Footer ── */}
                <div className="bg-white dark:bg-dark-900 px-6 py-3.5 flex items-center justify-between border-t border-slate-150 dark:border-dark-800 flex-shrink-0 gap-4">
                  {/* Created & Modified Audit Info */}
                  <div className="text-[11px] font-normal text-slate-500 dark:text-dark-400 space-y-0.5 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <ClockIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>Created: <strong className="text-slate-700 dark:text-dark-200 font-medium">{formatDisplayDate(selectedQuotation.CreatedAt || selectedQuotation.createdAt || selectedQuotation.CreatedDate || selectedQuotation.createdDate)}</strong></span>
                      {createdByName && (
                        <>
                          <span className="text-slate-300 dark:text-dark-600">•</span>
                          <UserIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>By <strong className="text-slate-700 dark:text-dark-200 font-medium">{createdByName}</strong></span>
                        </>
                      )}
                    </div>
                    {(selectedQuotation.ModifiedAt || selectedQuotation.modifiedAt || selectedQuotation.ModifiedDate || selectedQuotation.modifiedDate) && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <ClockIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>Modified: <strong className="text-slate-700 dark:text-dark-200 font-medium">{formatDisplayDate(selectedQuotation.ModifiedAt || selectedQuotation.modifiedAt || selectedQuotation.ModifiedDate || selectedQuotation.modifiedDate)}</strong></span>
                        {modifiedByName && (
                          <>
                            <span className="text-slate-300 dark:text-dark-600">•</span>
                            <UserIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>By <strong className="text-slate-700 dark:text-dark-200 font-medium">{modifiedByName}</strong></span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={onClose}
                    color="neutral"
                    variant="soft"
                    className="px-5 py-2 text-xs font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-dark-700 transition-colors shrink-0"
                  >
                    Close
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
